import type { SyncJobMetadata } from '@/stores/realtime-sync-diagnostics-store';
import { realtimeSyncEngine } from './engine';

export interface SyncPlugin<TScope = unknown, TOptions = undefined> {
  readonly key: string;
  createJobKey: (scope: TScope, options?: TOptions) => string;
  createMetadata: (scope: TScope, options?: TOptions) => SyncJobMetadata;
  run: (scope: TScope, options?: TOptions) => Promise<void>;
  shouldRetry?: (error: unknown) => boolean;
}

const plugins = new Map<string, SyncPlugin<unknown, unknown>>();

export function registerSyncPlugin<TScope, TOptions = undefined>(
  plugin: SyncPlugin<TScope, TOptions>,
): SyncPlugin<TScope, TOptions> {
  if (plugins.has(plugin.key)) {
    console.warn(`Sync plugin with key "${plugin.key}" is already registered.`);
    return plugin;
  }
  plugins.set(plugin.key, plugin as SyncPlugin<unknown, unknown>);
  return plugin;
}

export function enqueueSyncJob<TScope, TOptions = undefined>(
  pluginKey: string,
  scope: TScope,
  options?: TOptions,
): void {
  const plugin = plugins.get(pluginKey) as SyncPlugin<TScope, TOptions> | undefined;
  if (!plugin) {
    console.warn(`No sync plugin registered for key "${pluginKey}".`);
    return;
  }

  const jobKey = plugin.createJobKey(scope, options);
  const metadata = plugin.createMetadata(scope, options);

  realtimeSyncEngine.schedule(jobKey, () => plugin.run(scope, options), {
    metadata,
    shouldRetry: plugin.shouldRetry,
  });
}

export function getRegisteredSyncPlugins(): SyncPlugin[] {
  return [...plugins.values()];
}
